import { inspectionClient } from '@/config/api';





export interface ReportsFilters {
  startDate?: string;
  endDate?: string;
  providerId?: string;
  operationalProviderId?: string;
  inspectorId?: string;
  city?: string;
  inspectionTypeIds?: string[];
}






export interface KPIDataResponse {
  totalVistorias: number;
  totalVistoriasMesAnterior: number;
  taxaConclusao: number;
  taxaConclusaoMesAnterior: number;
  tempoMedioExecucao: number;
  tempoMedioExecucaoMesAnterior: number;
  vistoriasPendentes: number;
  vistoriasPendentesMesAnterior: number;
}


export interface StatusDistributionResponse {
  name: string;
  value: number;
  color: string;
}

export interface InspectionTypeDistributionResponse {
  name: string;
  value: number;
  color: string;
}


export interface TopInspectorResponse {
  inspectorId: string; 
  name: string;
  total: number;
}

export interface TopProviderResponse {
  providerId: string; 
  name: string;
  total: number;
}

export interface TopOperationalProviderResponse {
  operationalUserId: string; 
  operationalUserName: string;
  totalInspections: number;
  completedInspections: number;
  completionRate: number;
  averageCompletionTime: number;
}

export interface TopCityResponse {
  name: string;
  state: string;
  total: number;
}


export interface MonthlyEvolutionResponse {
  month: string; 
  year: number;
  total: number;
}

export interface CompletionTrendResponse {
  month: string;
  year: number;
  total: number;
  completed: number;
  completionRate: number;
}

export interface MonthlyStatusBreakdownResponse {
  month: string;
  year: number;
  status: string;
  count: number;
}

export interface YearComparisonResponse {
  month: string;
  year: number;
  count: number;
}


export interface RejectionRateResponse {
  providerId: string; 
  provider: string;
  rate: number; 
}

export interface OperationalRejectionRateResponse {
  operationalUserId: string; 
  operationalUserName: string;
  totalInspections: number;
  rejectedInspections: number;
  rejectionRate: number;
}

export interface InspectorProductivityResponse {
  inspectorId: string; 
  name: string;
  statusCounts: Record<string, number>;
  totalInspections: number;
  productivityScore: number;
  averageCompletionTime: number;
}

export interface ProviderComparisonResponse {
  providerId: string; 
  name: string;
  statusCounts: Record<string, number>;
  totalInspections: number;
  completionRate: number;
  averageCompletionTime: number;
}

export interface OperationalProviderComparisonResponse {
  operationalUserId: string; 
  operationalUserName: string;
  totalInspections: number;
  statusCounts: Record<string, number>;
  completionRate: number;
  averageCompletionTime: number;
}


export interface CityLocationResponse {
  city: string;
  state: string;
  lat: number;
  lng: number;
  count: number;
}

export interface InspectionLocationResponse {
  inspectionId: string;
  itemId: string;
  inspectionCode: string;
  inspectionType: string;
  status: string;
  statusDescription: string;
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  inspectorName?: string;
  providerName?: string;
  createdAt: string;
  scheduledDate?: string;
  completedDate?: string;
  evaluatedValue?: number;
}


export interface ProcessFunnelResponse {
  stage: string;
  count: number;
  percentage: number;
}

export interface VolumeQualityResponse {
  month: string;
  year: number;
  volume: number;
  qualidade: number; 
}





export interface DrillDownInspection {
  id: string;
  inspectionRequestId: string;
  code: string;
  type: string;
  status: string;
  statusDescription: string;
  city: string;
  state: string;
  inspectorName?: string;
  providerName?: string;
  createdAt: string;
  scheduledDate?: string;
  completedDate?: string;
}

export interface DrillDownResponse {
  items: DrillDownInspection[];
  totalCount: number;
}

export interface DrillDownFilters {

  statusName?: string;
  inspectionTypeName?: string;
  inspectorId?: string;
  providerId?: string;
  city?: string;
  month?: string;
  year?: number;


  startDate?: string;
  endDate?: string;
  inspectionTypeIds?: string[];


  pageNumber?: number;
  pageSize?: number;
}





export class ReportsService {

  
  
  

  
  async getKPIs(filters?: ReportsFilters): Promise<KPIDataResponse> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/kpis${params}`;
      const response = await inspectionClient.get<KPIDataResponse>(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  
  async getStatusDistribution(filters?: ReportsFilters): Promise<StatusDistributionResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/status-distribution${params}`;
      const response = await inspectionClient.get<any[]>(url);

      
      const { getStatusColor } = await import('@/pages/Reports/utils/chartColors');
      return response.data.map((item: any) => ({
        name: item.name,
        value: item.value,
        color: getStatusColor(item.name) 
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getInspectionTypeDistribution(filters?: ReportsFilters): Promise<InspectionTypeDistributionResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/inspection-type-distribution${params}`;
      const response = await inspectionClient.get<any[]>(url);

      
      const { getChartColors } = await import('@/pages/Reports/utils/chartColors');
      const colors = getChartColors(response.data.length);

      return response.data.map((item: any, index: number) => ({
        name: item.name,
        value: item.value,
        color: item.color || colors[index]
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getTopInspectors(filters?: ReportsFilters): Promise<TopInspectorResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/top-inspectors${params}`;
      const response = await inspectionClient.get<any[]>(url);

      
      return response.data.map((item: any) => ({
        inspectorId: item.inspectorId,
        name: item.inspectorName,
        total: item.totalInspections
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getTopProviders(filters?: ReportsFilters): Promise<TopProviderResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/top-providers${params}`;
      const response = await inspectionClient.get<any[]>(url);

      
      return response.data.map((item: any) => ({
        providerId: item.providerId,
        name: item.providerName,
        total: item.totalInspections
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getTopOperationalProviders(filters?: ReportsFilters): Promise<TopOperationalProviderResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/top-operational-providers${params}`;
      const response = await inspectionClient.get<any[]>(url);

      
      return response.data.map((item: any) => ({
        operationalUserId: item.operationalUserId,
        operationalUserName: item.operationalUserName,
        totalInspections: item.totalInspections,
        completedInspections: item.completedInspections,
        completionRate: item.completionRate,
        averageCompletionTime: item.averageCompletionTime
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getTopCities(filters?: ReportsFilters): Promise<TopCityResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/top-cities${params}`;
      const response = await inspectionClient.get<any[]>(url);

      
      return response.data.map((item: any) => ({
        name: item.city,
        state: item.state,
        total: item.totalInspections
      }));
    } catch (error) {
      throw error;
    }
  }

  
  
  

  
  async getMonthlyEvolution(filters?: ReportsFilters): Promise<MonthlyEvolutionResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/monthly-evolution${params}`;
      const response = await inspectionClient.get<any[]>(url);

      
      return response.data.map((item: any) => ({
        month: item.month,
        year: item.year,
        total: item.count
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getCompletionTrend(filters?: ReportsFilters): Promise<CompletionTrendResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/completion-trend${params}`;
      const response = await inspectionClient.get<CompletionTrendResponse[]>(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  
  async getMonthlyStatusBreakdown(filters?: ReportsFilters): Promise<MonthlyStatusBreakdownResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/monthly-status-breakdown${params}`;
      const response = await inspectionClient.get<MonthlyStatusBreakdownResponse[]>(url);



      const grouped = new Map<string, { month: string; year: number; statusCounts: Record<string, number> }>();

      response.data.forEach((item: any) => {
        const key = `${item.year}-${item.month}`;
        if (!grouped.has(key)) {
          grouped.set(key, {
            month: item.month,
            year: item.year,
            statusCounts: {}
          });
        }
        const monthData = grouped.get(key)!;
        monthData.statusCounts[item.status] = item.count;
      });

      return Array.from(grouped.values()).sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        
        return 0; 
      }) as any;
    } catch (error) {
      throw error;
    }
  }

  
  async getYearComparison(filters?: ReportsFilters): Promise<YearComparisonResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/year-comparison${params}`;
      const response = await inspectionClient.get<YearComparisonResponse[]>(url);



      const currentYear = new Date().getFullYear();
      const previousYear = currentYear - 1;

      const grouped = new Map<string, { month: string; anoAtual: number; anoAnterior: number }>();

      response.data.forEach((item: any) => {
        if (!grouped.has(item.month)) {
          grouped.set(item.month, {
            month: item.month,
            anoAtual: 0,
            anoAnterior: 0
          });
        }
        const monthData = grouped.get(item.month)!;
        if (item.year === currentYear) {
          monthData.anoAtual = item.count;
        } else if (item.year === previousYear) {
          monthData.anoAnterior = item.count;
        }
      });

      return Array.from(grouped.values()) as any;
    } catch (error) {
      throw error;
    }
  }

  
  
  

  
  async getRejectionRate(filters?: ReportsFilters): Promise<RejectionRateResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/rejection-rate${params}`;
      const response = await inspectionClient.get<any[]>(url);
      return response.data.map((item: any) => ({
        providerId: item.providerId,
        provider: item.providerName,
        rate: item.rejectionRate
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getOperationalRejectionRate(filters?: ReportsFilters): Promise<OperationalRejectionRateResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/operational-rejection-rate${params}`;
      const response = await inspectionClient.get<any[]>(url);
      return response.data.map((item: any) => ({
        operationalUserId: item.operationalUserId,
        operationalUserName: item.operationalUserName,
        totalInspections: item.totalInspections,
        rejectedInspections: item.rejectedInspections,
        rejectionRate: item.rejectionRate
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getInspectorProductivity(filters?: ReportsFilters): Promise<InspectorProductivityResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/inspector-productivity${params}`;
      const response = await inspectionClient.get<any[]>(url);
      return response.data.map((item: any) => ({
        inspectorId: item.inspectorId,
        name: item.inspectorName,
        statusCounts: item.statusCounts || {},
        totalInspections: item.totalInspections,
        productivityScore: item.productivityScore,
        averageCompletionTime: item.averageCompletionTime
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getProviderComparison(filters?: ReportsFilters): Promise<ProviderComparisonResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/provider-comparison${params}`;
      const response = await inspectionClient.get<any[]>(url);
      return response.data.map((item: any) => ({
        providerId: item.providerId,
        name: item.providerName,
        statusCounts: item.statusCounts || {},
        totalInspections: item.totalInspections,
        completionRate: item.completionRate,
        averageCompletionTime: item.averageCompletionTime
      }));
    } catch (error) {
      throw error;
    }
  }

  
  async getOperationalProviderComparison(filters?: ReportsFilters): Promise<OperationalProviderComparisonResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/operational-provider-comparison${params}`;
      const response = await inspectionClient.get<any[]>(url);
      return response.data.map((item: any) => ({
        operationalUserId: item.operationalUserId,
        operationalUserName: item.operationalUserName,
        totalInspections: item.totalInspections,
        statusCounts: item.statusCounts || {},
        completionRate: item.completionRate,
        averageCompletionTime: item.averageCompletionTime
      }));
    } catch (error) {
      throw error;
    }
  }

  
  
  

  
  async getCityLocations(filters?: ReportsFilters): Promise<CityLocationResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/city-locations${params}`;
      const response = await inspectionClient.get<CityLocationResponse[]>(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  
  async getInspectionLocations(filters?: ReportsFilters): Promise<InspectionLocationResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/inspection-locations${params}`;
      const response = await inspectionClient.get<InspectionLocationResponse[]>(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  
  
  

  
  async getProcessFunnel(filters?: ReportsFilters): Promise<ProcessFunnelResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/process-funnel${params}`;
      const response = await inspectionClient.get<ProcessFunnelResponse[]>(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  
  async getVolumeQuality(filters?: ReportsFilters): Promise<VolumeQualityResponse[]> {
    try {
      const params = this.buildQueryParams(filters);
      const url = `/api/Reports/volume-quality${params}`;
      const response = await inspectionClient.get<VolumeQualityResponse[]>(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  
  
  

  
  async getDrillDownData(drillDownFilters: DrillDownFilters): Promise<DrillDownResponse> {
    try {
      const params = new URLSearchParams();

      
      if (drillDownFilters.statusName) params.append('statusName', drillDownFilters.statusName);
      if (drillDownFilters.inspectionTypeName) params.append('inspectionTypeName', drillDownFilters.inspectionTypeName);
      if (drillDownFilters.inspectorId) params.append('inspectorId', drillDownFilters.inspectorId);
      if (drillDownFilters.providerId) params.append('providerId', drillDownFilters.providerId);
      if (drillDownFilters.city) params.append('city', drillDownFilters.city);
      if (drillDownFilters.month) params.append('month', drillDownFilters.month);
      if (drillDownFilters.year) params.append('year', drillDownFilters.year.toString());


      if (drillDownFilters.startDate) params.append('startDate', drillDownFilters.startDate);
      if (drillDownFilters.endDate) params.append('endDate', drillDownFilters.endDate);


      if (drillDownFilters.inspectionTypeIds && drillDownFilters.inspectionTypeIds.length > 0) {
        drillDownFilters.inspectionTypeIds.forEach(id => params.append('inspectionTypeIds', id));
      }


      if (drillDownFilters.pageNumber) params.append('pageNumber', drillDownFilters.pageNumber.toString());
      if (drillDownFilters.pageSize) params.append('pageSize', drillDownFilters.pageSize.toString());

      const queryString = params.toString();
      const url = queryString ? `/api/Reports/drill-down?${queryString}` : '/api/Reports/drill-down';

      const response = await inspectionClient.get<DrillDownResponse>(url);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  
  async getDrillDown(drillDownFilters: DrillDownFilters): Promise<DrillDownResponse> {
    return this.getDrillDownData(drillDownFilters);
  }

  
  
  

  
  private buildQueryParams(filters?: ReportsFilters): string {
    if (!filters) return '';

    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.providerId) params.append('providerId', filters.providerId);
    if (filters.operationalProviderId) params.append('operationalProviderId', filters.operationalProviderId);
    if (filters.inspectorId) params.append('inspectorId', filters.inspectorId);
    if (filters.city) params.append('city', filters.city);
    if (filters.inspectionTypeIds && filters.inspectionTypeIds.length > 0) {
      filters.inspectionTypeIds.forEach(id => params.append('inspectionTypeIds', id));
    }

    const queryString = params.toString();
    return queryString ? `?${queryString}` : '';
  }
}


export const reportsService = new ReportsService();
